/**
 * 試聴用 audio の排他再生。
 *
 * BGM 設定モーダル / 素材ピッカー / BGM セクション / フリー楽曲一覧など、試聴ボタンが同じ画面に複数あると
 * 2 曲同時に鳴ってしまうため、再生を始める前に `playExclusive` を通して直前に鳴らしていた試聴を止める。
 * 止められた側は自分の pause イベントで表示 (再生中アイコン) を戻す。
 *
 * `<audio>` 以外の試聴 (Web Audio で区間を鳴らす AudioRangeTrimmer 等) は `claimExclusive(stop)` で
 * 「止め方」を登録する。以後 `<audio>` の試聴が始まれば stop が呼ばれ、逆に claim 時には鳴っている
 * `<audio>` を止める。
 *
 * グローバルに全 <audio> を止める方式にしないのは、動画エディタのプレビュー (Remotion の audio/video) まで
 * 止めてしまうため。試聴プレイヤーだけがこの関数を opt-in で使う。
 */
let current: HTMLAudioElement | null = null;
let currentStop: (() => void) | null = null;

function stopOthers(except?: HTMLAudioElement): void {
  if (current && current !== except && !current.paused) {
    current.pause();
  }
  const stop = currentStop;
  currentStop = null;
  stop?.();
}

/** 他の試聴を止めてから再生する。play() の Promise をそのまま返す (autoplay 拒否などの失敗は呼び出し側で扱う) */
export function playExclusive(audio: HTMLAudioElement): Promise<void> {
  stopOthers(audio);
  current = audio;
  return audio.play();
}

/**
 * `<audio>` 以外の試聴を始める前に呼ぶ。鳴っている他の試聴を止め、自分の止め方を登録する
 * (次に別の試聴が始まったら stop が呼ばれる)。stop は何度呼ばれてもよいこと。
 */
export function claimExclusive(stop: () => void): void {
  stopOthers();
  current = null;
  currentStop = stop;
}

/** claimExclusive で登録した止め方を外す (自分で止めた / 破棄したとき)。他の登録は触らない。 */
export function releaseExclusive(stop: () => void): void {
  if (currentStop === stop) currentStop = null;
}

/** テスト用: 排他の状態をリセット */
export function resetExclusiveAudioForTests(): void {
  current = null;
  currentStop = null;
}
